/* eslint-disable import/no-internal-modules */
/**
 * App Entry Point
 *
 * @module Index
 */
import areIntlLocalesSupported from 'intl-locales-supported';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import { call, put } from 'redux-saga/effects';
import UAParser from 'ua-parser-js';

import { ApiPlaybackPaused } from './actions/Api';
import { initApp, initializeClient } from './actions/Application';
import { setCapabilities } from './actions/Capabilities';
import { relayExternalApiCall } from './actions/External';
import { closeMiniCoachmark } from './actions/MiniCoachmark';
import { setCurrentScreenDimensions, setSmallScreenUI } from './actions/Screen';
import { setScroll } from './actions/Scroll';
import { autoLogin } from './actions/UseCases';
import Fallback from './components/Fallback/Fallback';
import ScrollToTop from './components/lib/ScrollToTop/ScrollToTop';
import * as Constants from './constants';
import { ScreenKeys, ScreenThresholds } from './constants/Screen';
import { ScrollThresholds } from './constants/Scroll';
import { recalculateDomains } from './domains';
import { registerDomains } from './domains/domainRegistry';
import { getInDomain } from './domains/externalUtils';
import domains from './domains/loaders';
import { getBugsnagErrorBoundary, initBugsnag } from './lib/BugsnagClient';
import Detector from './lib/Detector';
import ApplicationError from './lib/error/ApplicationError';
import { getHistory } from './lib/history';
import { isLoggedIn } from './lib/ListenerUtils';
import PandoraApi from './lib/PandoraApi';
import { getCustomEventName } from './lib/PandoraApi/Secretary';
import { getNowPlayingPath } from './lib/RouteUtils';
import { getSentryErrorBoundary, initSentry } from './lib/SentryClient';
import { pipePromises } from './lib/utils/promises';
import mediator from './mediator';
import mobileRootRoute from './routes/mobileRoutes';
import rootRoute from './routes/Routes';
import { putAndWait } from './sagas/TaskManager';
import { createReduxStore, tryToRecoverFromStoreError } from './store/StoreUtils';
import Logger from './tools/Logger';
import * as Utils from './utils';

import './sass/global.scss';

const isSentryLoggingEnabled = get(window, '__CONFIG__.featureFlags.sentryLoggingEnabled', false);
isSentryLoggingEnabled ? initSentry() : initBugsnag();
const log = Logger.child({ component: 'Index' });

const MOBILE = 'mobile';
const TABLET = 'tablet';

let isScrollThrottled = false;
let lastIgnoredScroll = false;

// Expose the Pandora API for ads as a global
window.Pandora = PandoraApi;
window.isElectron = function () {
  return 'DesktopBridge' in window;
};

// Allows "window.top" from files originate on the CDN
document.domain = Utils.Url.domainWithoutSubdomain();

const uaParser = new UAParser();
const isMobileWeb = uaParser.getDevice().type === MOBILE;
const isTablet = uaParser.getDevice().type === TABLET;

// Called by <Router/> to create a route's components. <Router/> injects extra props
// that we can use here to conditionally apply keys to components to trigger animations
function createElement(Component, propsFromRouter) {
  if ('/' + propsFromRouter.route.path === getNowPlayingPath()) {
    return <Component {...propsFromRouter} {...propsFromRouter.route.props} key='NowPlaying' />;
  }
  return <Component {...propsFromRouter} {...propsFromRouter.route.props} />;
}

function handleExternalApiEvent(event, store) {
  const params = event.detail;
  store.dispatch(relayExternalApiCall(params));
}

function setScrollPos(store) {
  if (!isScrollThrottled) {
    isScrollThrottled = true;
    lastIgnoredScroll = false;

    requestAnimationFrame(() => {
      store.dispatch(setScroll(window.pageYOffset));
    });

    setTimeout(() => {
      isScrollThrottled = false;

      if (typeof lastIgnoredScroll === 'number') {
        setScrollPos(store);
      }
    }, ScrollThresholds.SCROLL_THROTTLE);
  } else {
    lastIgnoredScroll = window.pageYOffset;
  }
}

function onWindowResize(store) {
  debounce(() => {
    const state = store.getState();
    const isScreenSmall = state.ui.get(ScreenKeys.IS_SCREEN_SMALL);

    store.dispatch(setCurrentScreenDimensions(window.innerWidth, window.innerHeight));

    if (
      window.innerHeight < ScreenThresholds.RIGHTRAIL_MIN_SCREEN_HEIGHT ||
      window.innerWidth < ScreenThresholds.RIGHTRAIL_MIN_SCREEN_WIDTH
    ) {
      if (!isScreenSmall) {
        store.dispatch(setSmallScreenUI(true));
      }
    } else {
      if (isScreenSmall) {
        store.dispatch(setSmallScreenUI(false));
      }
    }
  }, ScreenThresholds.SCREEN_RESIZE_DEBOUNCE)();
}

function onAppRendered(store) {
  // clear timeout that would otherwise display error state in case we didn't get this far
  clearTimeout(window.appNotLoadingTimeout);
  window.appNotLoadingTimeout = 'app initialized';

  // listen for external api events
  window.addEventListener(getCustomEventName(), event => handleExternalApiEvent(event, store));

  // listener to update global-level scroll position state
  window.addEventListener('scroll', () => setScrollPos(store));

  // listen to window resize events
  window.addEventListener('resize', () => onWindowResize(store));

  window.addEventListener('unload', () => {
    // signal to the server that the listener stopped listening to the stream here if necessary
    if (
      getInDomain('playback.utils.isAudioPlaying', false)(store.getState()) &&
      isLoggedIn(store.getState())
    ) {
      store.dispatch(ApiPlaybackPaused(false));
    }

    // Prevent any open mini-coachmark from automatically opening when re-loading the app
    store.dispatch(closeMiniCoachmark());
  });
}

function initDetector(params) {
  return Detector.start()
    .catch(error => {
      log.error(error, 'Failed to initialize Detector script.');
    })
    .finally(() => {
      return params;
    });
}

/**
 * Initializes internationalization support for browsers that do not support it natively
 *
 * @returns {Promise}
 */
function initIntl() {
  // @todo [WEBCA-1269] We should research a better solution for checking Intl support and providing a polyfill
  return new Promise((resolve, reject) => {
    const LOCALE = 'en';
    if (window.Intl && areIntlLocalesSupported([LOCALE])) {
      resolve();
    } else {
      System.import(/* webpackChunkName: "intl" */ 'intl')
        .then(System.import(/* webpackChunkName: "intl-en" */ 'intl/locale-data/jsonp/en.js'))
        .then(resolve)
        .catch(reject);
    }
  });
}

/**
 * Notifies the app of store initialization
 *
 * @param {object} options - options object
 * @param {object} options.store - store
 * @param {Function} options.rootSaga - rootSaga
 * @param {Function} options.epic$ - rootEpic
 * @returns {Promise}
 */
function onStoreInitialized({ store, rootSaga, epic$ }) {
  log.info('onStoreInitialized(): store initialized');
  Utils.Time.recordTimeAtMoment(Constants.App.TimingMoments.STORE_INITIALIZED);
  onWindowResize(store);
  return Promise.resolve({ epic$, rootSaga, store });
}

function initClient({ store, rootSaga, history }) {
  return new Promise(resolve => {
    const browser = uaParser.getBrowser();
    const ua = {
      browser,
      os: uaParser.getOS(),
    };
    const browserSupportLevel = Utils.ClientInfo.calculateBrowserSupportLevel(
      store.getState(),
      browser,
    );
    const isElectron = Utils.ClientInfo.isElectron();
    store.dispatch(initializeClient(ua, browserSupportLevel, isElectron));
    log.info('initClient(): client initialized');
    resolve({ history, rootSaga, store });
  });
}

/**
 * Initializes history object
 *
 * @param {object} options - options object
 * @param {object} options.store - store
 * @param {Function} options.rootSaga- rootSaga
 * @param {Function} options.epic$- epic$
 * @returns {Promise}
 */
function initHistory({ store, rootSaga, epic$ }) {
  const history = getHistory(store); // get synced store sync history
  log.info('initHistory(): history initialized');
  return Promise.resolve({ epic$, history, rootSaga, store });
}

/**
 * Initializes and starts application sagas
 *
 * @param {object} options - options object
 * @param {object} options.rootSaga - rootSaga
 * @param {object} options.store - store
 * @param {object} options.history - history object
 * @returns {Promise}
 */
function initSagas({ store, rootSaga, history }) {
  return store
    .runSaga(rootSaga)
    .toPromise()
    .then(() => {
      log.info('initSagas(): sagas initialized');
      return Promise.resolve({ history, store });
    });
}

/**
 * Loads "sync" domains or those required to be present before the application starts
 *
 * @param {object} options - options object
 * @param {object} options.store - store
 * @param {object} options.history - history object
 * @returns {Promise}
 */
function initDomains({ store, history }) {
  registerDomains(domains);

  store.dispatch(
    setCapabilities({
      isMobile: isMobileWeb,
      isTablet,
    }),
  );

  return store
    .runSaga(function* () {
      yield call(recalculateDomains);
    })
    .toPromise()
    .then(() => {
      return { history, store };
    });
}

/**
 * Starts mediator after precalculated domains are loaded and started
 *
 * @param {object} options - options object
 * @param {object} options.store - store
 * @param {object} options.history - history object
 * @returns {Promise}
 */
function initMediator({ store, history }) {
  return store
    .runSaga(mediator)
    .toPromise()
    .then(() => {
      return Promise.resolve({ history, store });
    });
}

/**
 * Notifies subscribers of application initialization through APPLICATION_INITIALIZE
 * action.
 *
 * @param {object} options - options object
 * @param {object} options.store - store
 * @param {object} options.history - history object
 * @param {object} options.epic$
 * @returns {Promise}
 */
function onAppInitialized({ store, history, epic$ }) {
  return store
    .runSaga(function* () {
      yield call(putAndWait, autoLogin());
      log.info('onAppInitialized(): application initialized');
      Utils.Time.recordTimeAtMoment(Constants.App.TimingMoments.APP_INITIALIZED);
      yield put(initApp(store));
      return Promise.resolve({ epic$, history, store });
    })
    .toPromise();
}

/**
 * Bootstraps all auxiliary logic required before starting the application
 *
 * @returns {Promise} - init status
 */
function initApplication() {
  return pipePromises(
    initDetector,
    initIntl,
    createReduxStore,
    onStoreInitialized,
    initHistory,
    initSagas,
    initClient,
    initDomains,
    initMediator,
    onAppInitialized,
  )().catch(e => {
    // TypeError indicates we're treating something like a type that it's not
    // this is likely to happen when for instance the state is somehow a bunch of objects
    // rather than immutable maps
    if (e instanceof TypeError) {
      log.warn(
        'Encountered TypeError while trying to start application. Attempting to purge persistence.',
      );
      tryToRecoverFromStoreError();
    }
    throw new ApplicationError(`unable to initialize application: ${e}`);
  });
}

/**
 * Gets the info for the Error Boundary component
 *
 * @returns {object}
 */
function getErrorBoundaryInfo() {
  return isSentryLoggingEnabled
    ? {
        ErrorBoundary: getSentryErrorBoundary(),
        props: { fallback: Fallback },
      }
    : {
        ErrorBoundary: getBugsnagErrorBoundary(),
        props: { FallbackComponent: Fallback },
      };
}

/**
 * Creates the App Component
 *
 * @param {object} store - The application store
 * @param {object} history - Application history
 * @param {object} rootEpic$ - redux observable root epic
 */
function App({ store, history, rootEpic$ }) {
  // render root element
  const rootGeneratorFunction = isMobileWeb || isTablet ? mobileRootRoute : rootRoute;

  const { ErrorBoundary, props } = getErrorBoundaryInfo();

  return (
    <ErrorBoundary {...props}>
      <Provider store={store} rootEpic$={rootEpic$}>
        <Router
          history={history}
          routes={rootGeneratorFunction(store, isMobileWeb, isTablet)}
          createElement={createElement}
        />
        <ScrollToTop />
      </Provider>
    </ErrorBoundary>
  );
}

App.propTypes = {
  store: PropTypes.object,
  history: PropTypes.object,
  rootEpic$: PropTypes.object,
};

/**
 * Dispatches required actions and renders the initial DOM
 *
 * @param {object} store - The application store
 * @param {object} history - Application history
 * @param {object} rootEpic$ - redux observable root epic
 */
function renderApp(props) {
  ReactDOM.render(<App {...props} />, window.document.querySelector('.Container'), () =>
    onAppRendered(props.store),
  );
}

// Extra Comment
/**
 * Entry point of the application
 *
 * @returns {Promise} - start status
 */
function startApp() {
  Utils.Time.recordTimeAtMoment(Constants.App.TimingMoments.START_APP);

  return pipePromises(initApplication, renderApp)().catch(e => {
    // Communicate with the index.vm for any handling it wants to do
    // since unfortunately unhandledrejection doesn't have good browser support
    const unhandledEvent = new CustomEvent('unhandledInitError');
    window.dispatchEvent(unhandledEvent);
    throw new ApplicationError(`unable to start application: ${e}`);
  });
}

// Start the app
startApp();
